import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { operationService } from '../services/operationService';
import { Operation, Team, AuditLog } from '../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEye, faChartLine } from '@fortawesome/free-solid-svg-icons';
import OperationOverview from '../components/OperationOverview';
import OperationAnalysis from '../components/OperationAnalysis';
import api from '../services/api';

const OperationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  // Check permissions
  const canViewAnalytics = user?.permissions?.['firehub-super-admin'] || user?.permissions?.['asm-manage-analytics'];
  const canGenerateReports = user?.permissions?.['firehub-super-admin'] || user?.permissions?.['asm-manage-reports'];
  
  const [operation, setOperation] = useState<Operation | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'analysis'>('overview');
  const [generatingReport, setGeneratingReport] = useState(false);

  useEffect(() => {
    const loadOperationData = async () => {
      if (!id || !user?.orgID) return;

      try {
        setLoading(true);
        setError('');

        // Load operation details
        const operationData = await operationService.getOperation(user.orgID, parseInt(id));
        if (operationData && operationData.length > 0) {
          setOperation(operationData[0]);
        }

        // Load teams
        const teamsData = await operationService.getTeamsByOperation(user.orgID, parseInt(id));
        setTeams(teamsData);

        // Load audit logs
        const auditLogsData = await operationService.getAuditLogsByOperation(user.orgID, parseInt(id));
        setAuditLogs(auditLogsData);

      } catch (error: any) {
        setError(error.message || 'Fehler beim Laden der Einsatzdaten');
        console.error('Error loading operation data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadOperationData();
  }, [id, user?.orgID]);

  // Set active tab based on URL parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'analysis') {
      setActiveTab('analysis');
    } else {
      setActiveTab('overview');
    }
  }, [searchParams]);

  const handleGenerateReport = async () => {
    if (!operation || !user?.orgID) return;

    try {
      setGeneratingReport(true);
      const blob = await operationService.generateReport(user.orgID, operation.id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Einsatz-${operation.opsNumber}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error generating report:', error);
      alert('Fehler beim Generieren des Berichts: ' + error.message);
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleUpdateOperation = async (updatedOperation: Partial<Operation>) => {
    if (!operation || !user?.orgID) return;

    try {
      // Prepare the update payload according to PWA API specification
      const updatePayload: any = {
        orgID: user.orgID
      };

      // Add location if provided
      if (updatedOperation.location !== undefined) {
        updatePayload.location = updatedOperation.location;
        updatePayload.ort = updatedOperation.location;
      }

      // Add category/type if provided
      if (updatedOperation.categoryID !== undefined) {
        updatePayload.type = updatedOperation.categoryID;
      }

      // Add category name if provided
      if (updatedOperation.category !== undefined) {
        updatePayload.category = updatedOperation.category;
        updatePayload.typ = updatedOperation.category;
        updatePayload.alertTitle = updatedOperation.category;
      }

      // Add title and description if both location and category are provided
      if (updatedOperation.location && updatedOperation.category) {
        updatePayload.title = `${updatedOperation.location}: ${updatedOperation.category}`;
        updatePayload.description = `${updatedOperation.category} an ${updatedOperation.location}`;
      }

      // Update local state immediately (like PWA does)
      const newLocation = updatedOperation.location !== undefined ? updatedOperation.location : operation.location;
      const newCategory = updatedOperation.category !== undefined ? updatedOperation.category : operation.category;
      const newCategoryID = updatedOperation.categoryID !== undefined ? updatedOperation.categoryID : operation.categoryID;
      
      const updatedOperationData = {
        ...operation,
        location: newLocation,
        category: newCategory,
        categoryID: newCategoryID,
        // Update title based on new location and category
        title: `${newLocation}: ${newCategory}`,
        // Update other related fields
        alertTitle: newCategory,
        typ: newCategory,
        ort: newLocation
      };
      
      setOperation(updatedOperationData);

      // Try API update - but don't fail if it errors
      try {
        const response = await api.put(`/api/v1/operations/${operation.id}`, updatePayload);
      } catch (error: any) {
        console.warn('⚠️ API Update fehlgeschlagen, aber lokal gespeichert:', error.response?.status, error.message);
        // Don't throw error - data is already saved locally
      }
      
    } catch (error: any) {
      console.error('Error updating operation:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        fontSize: '18px',
        color: '#666'
      }}>
        Einsatzdaten werden geladen...
      </div>
    );
  }

  if (error) {
    return (
      <div className="portal-error">
        <strong>Fehler:</strong> {error}
      </div>
    );
  }

  if (!operation) {
    return <div className="portal-empty">Einsatz nicht gefunden</div>;
  }

  return (
    <div className="portal-stack">
      <div className="portal-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
          <button type="button" className="portal-btn portal-btn--primary" onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faArrowLeft} />
            Zurück
          </button>
          <div style={{ minWidth: 0 }}>
            <h1 className="portal-page-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {operation.title || `${operation.location}: ${operation.alertTitle}`}
            </h1>
          </div>
        </div>
      </div>

      <div className="portal-card">
        <div className="portal-seg" style={{ marginBottom: 18 }}>
          <button
            type="button"
            className={`portal-seg-btn${activeTab === 'overview' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <FontAwesomeIcon icon={faEye} />
            Übersicht
          </button>
          {canViewAnalytics && (
            <button
              type="button"
              className={`portal-seg-btn${activeTab === 'analysis' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('analysis')}
            >
              <FontAwesomeIcon icon={faChartLine} />
              Analyse
            </button>
          )}
        </div>

        {operation.lockStatus === 2 && (
          <div className="portal-card" style={{
            marginBottom: 16,
            padding: 14,
            background: '#fffbf0',
            borderColor: '#ffeaa7',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <span style={{ fontSize: 20 }}>🔐</span>
            <div>
              <div style={{ fontWeight: 800, color: '#856404' }}>Einsatz ist versiegelt</div>
              <div className="portal-hint" style={{ color: '#856404' }}>Dieser Einsatz kann nicht mehr bearbeitet werden.</div>
            </div>
          </div>
        )}

        {activeTab === 'overview' && (
          <OperationOverview
            operation={operation}
            teams={teams}
            onGenerateReport={canGenerateReports ? handleGenerateReport : () => {}}
            onUpdateOperation={operation.lockStatus === 2 ? undefined : handleUpdateOperation}
          />
        )}

        {activeTab === 'analysis' && canViewAnalytics && (
          <OperationAnalysis
            operation={operation}
            teams={teams}
            auditLogs={auditLogs}
          />
        )}
      </div>
    </div>
  );
};

export default OperationDetail; 